import React from 'react'

import type { RbacAction, RbacSubject } from './constants'
import { usePermission } from './usePermission'

interface Props {
  action: RbacAction
  subject: RbacSubject
  /** Rendered when the user lacks the ability. Defaults to nothing. */
  fallback?: React.ReactNode
  children: React.ReactNode
}

/**
 * Renders `children` only when the current user's DB-driven ability allows
 * `action` on `subject`. Renders nothing while the ability is still loading (no
 * flash), then `children` or `fallback`.
 *
 * UI ergonomics only — the EPS AbilityGuard is the real boundary. Use to
 * hide menu tiles, buttons, links the user can't act on.
 *
 * @example
 *   <RequirePermission action="Read" subject="FundingInvoice">
 *     <NavLink to="/funding-invoices">Funding Invoices</NavLink>
 *   </RequirePermission>
 */
export default function RequirePermission({
  action,
  subject,
  fallback = null,
  children,
}: Props) {
  const { allowed, isLoading } = usePermission(action, subject)
  if (isLoading) return null
  return <>{allowed ? children : fallback}</>
}
